import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  CheckCircle,
  ArrowLeft,
  RotateCcw,
  ThumbsUp,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import {
  useAuditReportDetail,
  useApproveReport,
  useRequestRevision,
} from "@/hooks/api/useAudit";
import { useBackendAuthenticated } from "@/hooks/useBackendAuthenticated";
import type { AiFinding, FieldSource, FieldSources, TemplateField } from "@arna-med/shared";
import { resolveFieldLabel } from "@/lib/reportFields";
import { SourceBadge } from "@/components/report/SourceBadge";

function AudioPlayer({ reportId }: { reportId: string }) {
  const { t } = useTranslation();
  const backend = useBackendAuthenticated();
  const blobUrlRef = useRef<string | null>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMissing(false);
    backend<Blob>({ method: "GET", url: `/api/v1/audit/reports/${reportId}/audio`, responseType: "blob" })
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setSrc(url);
      })
      .catch(() => {
        if (!cancelled) setMissing(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [reportId, backend]);

  if (loading) return <Spinner className="h-4 w-4" />;
  if (missing) return <p className="text-sm text-muted-foreground">{t("audit.noAudio")}</p>;
  return <audio controls src={src ?? undefined} className="w-full" />;
}

function AiFindingItem({ finding, fields }: { finding: AiFinding; fields?: TemplateField[] }) {
  const { t, i18n } = useTranslation();
  const isError = finding.severity === "error";
  return (
    <div
      className={`flex items-start gap-2 p-3 rounded-md border text-sm ${
        finding.fixed
          ? "bg-green-50 border-green-200 opacity-70"
          : isError
            ? "bg-red-50 border-red-200"
            : "bg-yellow-50 border-yellow-200"
      }`}
    >
      {finding.fixed ? (
        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
      ) : (
        <AlertTriangle
          className={`w-4 h-4 mt-0.5 shrink-0 ${isError ? "text-red-500" : "text-yellow-500"}`}
        />
      )}
      <div className="flex-1">
        <span className="font-medium text-xs text-muted-foreground uppercase mr-2">
          {resolveFieldLabel(finding.field, fields, i18n.language, t)}
        </span>
        {finding.issue}
        {finding.fixed && (
          <span className="ml-2 text-xs text-green-600 font-medium">
            ✓ {t("audit.aiFixed")}
          </span>
        )}
      </div>
    </div>
  );
}

export default function AuditReportDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isLoading } = useAuditReportDetail(id);
  const approve = useApproveReport();
  const requestRevision = useRequestRevision();

  const [revisionNotes, setRevisionNotes] = useState("");
  const [showRevisionForm, setShowRevisionForm] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(false);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center py-20">
          <Spinner />
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-6">
          <p className="text-muted-foreground">Report not found.</p>
        </div>
      </Layout>
    );
  }

  const { report, consultation, transcriptions, doctor, patient, audioFile } = data;
  const aiFindings = (report.aiFindings as AiFinding[] | null) ?? [];
  const isPendingAudit = report.auditStatus === "pending_audit";

  const handleApprove = async () => {
    if (!confirmApprove) {
      setConfirmApprove(true);
      return;
    }
    await approve.mutateAsync(report.id);
    navigate("/auditor");
  };

  const handleRevision = async () => {
    if (!showRevisionForm) {
      setShowRevisionForm(true);
      return;
    }
    if (!revisionNotes.trim()) return;
    await requestRevision.mutateAsync({ id: report.id, auditorNotes: revisionNotes });
    navigate("/auditor");
  };

  const mergedReportData = {
    ...(report.structuredData as Record<string, unknown>),
    ...(report.editedData as Record<string, unknown> ?? {}),
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate("/auditor")}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t("common.back")}
          </Button>
          <div>
            <h1 className="text-xl font-semibold">{consultation.patientName}</h1>
            <p className="text-sm text-muted-foreground">
              {doctor.name} ·{" "}
              {new Date(consultation.startedAt).toLocaleDateString()}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Report data + AI Findings */}
          <div className="space-y-4">
            {/* AI Findings */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <h2 className="font-medium flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500" />
                  {t("audit.aiFindingsTitle")}
                  {aiFindings.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      ({aiFindings.length})
                    </span>
                  )}
                </h2>
                {aiFindings.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("audit.noFindings")}</p>
                ) : (
                  <div className="space-y-2">
                    {aiFindings.map((f, i) => (
                      <AiFindingItem
                        key={i}
                        finding={f}
                        fields={report.templateSnapshot?.fields as TemplateField[] | undefined}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Report fields */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <h2 className="font-medium">{t("audit.reportDetail")}</h2>
                {patient && (
                  <div className="text-sm text-muted-foreground">
                    {patient.name} · {patient.personalNumber}
                  </div>
                )}
                <div className="space-y-2">
                  {Object.entries(mergedReportData).map(([key, value]) => (
                    <div key={key} className="text-sm">
                      <span className="font-medium text-muted-foreground capitalize">{key.replace(/_/g, " ")}:</span>{" "}
                      <span>
                        {typeof value === "object" ? JSON.stringify(value) : String(value ?? "—")}
                      </span>
                      {/* The auditor is the reader with the most reason to know
                          which lines the doctor dictated and which the system
                          supplied — a confirmed standard status reads exactly
                          like an observed one otherwise. */}
                      <SourceBadge
                        source={
                          ((report.fieldSources ?? {}) as FieldSources)[key] as
                            | FieldSource
                            | undefined
                        }
                      />
                    </div>
                  ))}
                </div>
                {report.doctorNotes && (
                  <div className="pt-2 border-t text-sm">
                    <span className="font-medium text-muted-foreground">Doctor notes:</span>{" "}
                    {report.doctorNotes}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right: Transcript + Audio + Actions */}
          <div className="space-y-4">
            {/* Transcript */}
            <Card>
              <CardContent className="p-4 space-y-2">
                <h2 className="font-medium">{t("audit.transcript")}</h2>
                {transcriptions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("audit.noTranscript")}</p>
                ) : (
                  <div className="text-sm whitespace-pre-wrap max-h-64 overflow-y-auto bg-muted/30 rounded p-3">
                    {transcriptions.map((tx) => tx.text).join("\n")}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Audio recording */}
            {audioFile && (
              <Card>
                <CardContent className="p-4 space-y-2">
                  <h2 className="font-medium">{t("audit.audio")}</h2>
                  <AudioPlayer reportId={report.id} />
                </CardContent>
              </Card>
            )}

            {/* Auditor actions */}
            {isPendingAudit && (
              <Card>
                <CardContent className="p-4 space-y-4">
                  <h2 className="font-medium">{t("audit.reportsTable.actions")}</h2>

                  {/* Approve */}
                  <div className="space-y-2">
                    {confirmApprove && (
                      <p className="text-sm text-muted-foreground bg-yellow-50 border border-yellow-200 rounded p-2">
                        {t("audit.confirmApprove")}
                      </p>
                    )}
                    <Button
                      className="w-full"
                      onClick={handleApprove}
                      disabled={approve.isPending}
                    >
                      <ThumbsUp className="w-4 h-4 mr-2" />
                      {confirmApprove ? t("common.confirm") : t("audit.approve")}
                    </Button>
                    {confirmApprove && (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => setConfirmApprove(false)}
                      >
                        {t("common.cancel")}
                      </Button>
                    )}
                  </div>

                  {/* Request Revision */}
                  <div className="space-y-2">
                    {showRevisionForm && (
                      <Textarea
                        placeholder={t("audit.auditorNotes")}
                        value={revisionNotes}
                        onChange={(e) => setRevisionNotes(e.target.value)}
                        rows={3}
                      />
                    )}
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={handleRevision}
                      disabled={requestRevision.isPending || (showRevisionForm && !revisionNotes.trim())}
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      {showRevisionForm ? t("common.submit") : t("audit.requestRevision")}
                    </Button>
                    {showRevisionForm && (
                      <Button
                        variant="ghost"
                        className="w-full"
                        onClick={() => {
                          setShowRevisionForm(false);
                          setRevisionNotes("");
                        }}
                      >
                        {t("common.cancel")}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Revision notes (if already decided) */}
            {report.auditorNotes && !isPendingAudit && (
              <Card>
                <CardContent className="p-4">
                  <h2 className="font-medium text-sm text-muted-foreground mb-1">
                    {t("audit.revisionNotes")}
                  </h2>
                  <p className="text-sm">{report.auditorNotes}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
